import Stack from "@mui/material/Stack";
import { observer } from "mobx-react-lite";
import { DropzoneOptions, useDropzone, type Accept } from "react-dropzone";
import useStore from "reusable/mobx-store-utils";
import { useTheme } from "theme/useTheme";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { useThemeStore } from "theme/ThemeStore";
import { FileUploadStore, FileUploadStoreStoreProvider, UploadStatus } from "./FileUploadStore";
import { useWebSocketStore } from "websockets/WebSocketStore";
import { useOptionalOrgGuardStore } from "features/main/main-router/org-guard/OrgGuardStore";
import { notNull } from "@gap/utils/utils";
import FileUploadBlock from "./FileUploadBlock";
import FileUploadAvatar from "./FileUploadAvatar";
import Box from "@mui/material/Box";
import { useEffect } from "react";
import { FileAcceptMap } from "@gap/utils/file/file-utils"

function fileAcceptMapToAccept(map: FileAcceptMap | undefined): Accept | undefined {
   if (map === undefined) return undefined
   const result: Accept = {}
   for (const key of Object.keys(map)) {
      const extensions = map[key as keyof FileAcceptMap]
      if (extensions !== undefined) {
         result[key] = extensions
      }
   }
   return result
}

export type FileUploadWithProgressProps = {
   files: File[],
   /** Must be provided when using outside the OrgGuardStore */
   fileOrgId?: string,
   /** Must be provided when using outside the OrgGuardStore */
   authUserId?: string
   accept?: FileAcceptMap
   subtext?: React.ReactNode,
   appendUlids?: boolean,
   onFilesValidated?: (files: File[]) => any,
   onLastFileRemoved?: () => void,
   onFileRemove?: (fileName: string) => void,
   getPresignedUrl: (file: File) => Promise<string>,
   /** Function that returns the url of a file that has been uploaded. Ex. so that we can directly see an uploaded image */
   getUploadedFileUrl?: (file: File) => Promise<string>
   formatFile?: boolean
   onUploadStarted?: () => void
   /** When provided, files for which this returns true keep showing an indeterminate progress + "processing…" label even after the upload itself is done. */
   isFileProcessing?: (file: File) => boolean
} & Omit<DropzoneOptions, 'onDropAccepted' | 'noClick' | 'multiple' | 'accept'>

export default observer(function FileUploadWithProgress({
   files,
   fileOrgId,
   subtext,
   accept,
   appendUlids,
   onFilesValidated,
   getPresignedUrl,
   formatFile,
   authUserId,
   isFileProcessing,
   ...dropzoneProps
}: FileUploadWithProgressProps) {
   const websocketStore = useWebSocketStore()
   const optionalOrgGuardStore = useOptionalOrgGuardStore()
   const orgId = fileOrgId || notNull(optionalOrgGuardStore, 'OrgGuardStore must exists when not supplying fileOrgId').userOrg.orgId
   const fileUploadStore = useStore((p) => new FileUploadStore(p), {
      ...dropzoneProps,
      formatFile,
      files,
      fileOrgId: orgId,
      appendUlids,
      onFilesValidated,
      getPresignedUrl,
      websocketStore,
      authUserId: authUserId || notNull(optionalOrgGuardStore, 'OrgGuardStore must exists when not supplying authUserId').userData.id
   })

   const maxFiles = (typeof dropzoneProps.maxFiles === 'undefined' || dropzoneProps.maxFiles > 1) ? dropzoneProps.maxFiles : 1

   const dropzone = useDropzone({
      ...dropzoneProps,
      accept: fileAcceptMapToAccept(accept),
      multiple: !!(typeof dropzoneProps.maxFiles === 'undefined' || dropzoneProps.maxFiles > 1),
      maxFiles,
      onDropAccepted: fileUploadStore.onDropAccepted,
      onDropRejected: fileUploadStore.onDropRejected,
      noClick: true,
   })

   useEffect(() => {
      fileUploadStore.setFiles(files)
   }, [fileUploadStore, files])

   const theme = useTheme()
   const themeStore = useThemeStore()
   const borderColour = '#A797A8'

   return (
      <FileUploadStoreStoreProvider store={fileUploadStore}>
         {fileUploadStore.activeUploads.map((activeUpload) => {
            return <Box key={activeUpload.file.name} sx={{ marginBottom: 2 }}>
               <FileUploadBlock
                  uploadedFile={activeUpload}
                  formatFile={formatFile}
                  isProcessing={isFileProcessing?.(activeUpload.file)}
               />
            </Box>
         })}
         {(!maxFiles || fileUploadStore.activeUploads.length < maxFiles) && <Stack sx={{
            borderRadius: '4px',
            border: `1px dashed ${borderColour}`
         }} {...dropzone.getRootProps()}>
            <input {...dropzone.getInputProps()} />
            <Stack sx={{
               flexDirection: 'column',
               gap: theme.spacing(1),
               alignItems: 'center',
               textAlign: 'center',
               py: 3,
               px: 1,
               transition: 'transform ease .25s, opacity ease .25s',
               ...(dropzone.isDragActive ? { transform: 'scale(.9)' } : {}),
               ...(dropzone.isDragReject ? { opacity: .6 } : {})
            }}>
               <FileUploadAvatar status={dropzone.isDragReject ? UploadStatus.invalid : UploadStatus.uploading} />
               <Typography variant="subtitle1" sx={{
                  display: 'inline-flex',
                  columnGap: '.3rem',
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center' },
               }}>
                  <Link
                     component="button"
                     variant="subtitle1"
                     onClick={dropzone.open}
                     sx={{ textDecoration: 'underline dashed 1px', textUnderlineOffset: '.4rem', textDecorationColor: borderColour }}
                  >
                     Click to upload
                  </Link>
                  <Typography variant="subtitle1" component="span">or drag and drop</Typography>
               </Typography>

               {fileUploadStore.validationText
                  ? <Typography
                     variant="body2"
                     color={themeStore.theme.palette.error.light}
                  >
                     {fileUploadStore.validationText}
                  </Typography>
                  : <Typography
                     variant="body2"
                     color={themeStore.theme.palette.primary.light}
                  >
                     {subtext}
                  </Typography>}
            </Stack>
         </Stack>}
      </FileUploadStoreStoreProvider>
   );
})
